import { NAV_ENTRIES, type NavEntry, visibleNavEntriesForPersona } from '@/shared/ui/layout'

export type FeatureOption = { value: string; label: string }

export type FeaturePersona = 'club-manager' | 'outfitter-owner'

function collectDestinations(entries: NavEntry[], acc: FeatureOption[], seen: Set<string>): void {
  for (const entry of entries) {
    if (entry.to && !seen.has(entry.id)) {
      seen.add(entry.id)
      acc.push({ value: entry.id, label: entry.label })
    }
    if (entry.children) collectDestinations(entry.children, acc, seen)
  }
}

export function deriveFeatureOptions(persona: FeaturePersona): FeatureOption[] {
  const options: FeatureOption[] = []
  collectDestinations(visibleNavEntriesForPersona(persona), options, new Set())
  options.push({ value: 'other', label: 'Other' })
  return options
}

function flattenDestinations(entries: NavEntry[]): NavEntry[] {
  return entries.flatMap((entry) => [
    ...(entry.to ? [entry] : []),
    ...(entry.children ? flattenDestinations(entry.children) : []),
  ])
}

export function deriveFeatureFromPathname(pathname: string, options: FeatureOption[]): string {
  const matches = flattenDestinations(NAV_ENTRIES).filter(
    (entry) => entry.to && (pathname === entry.to || pathname.startsWith(`${entry.to}/`)),
  )
  const best = matches.reduce<NavEntry | null>((longest, entry) => {
    const entryLength = entry.to?.length ?? 0
    const longestLength = longest?.to?.length ?? -1
    return entryLength > longestLength ? entry : longest
  }, null)
  const derived = best?.id ?? 'other'
  return options.some((option) => option.value === derived) ? derived : 'other'
}

export function personaForOrgType(orgType: 'club' | 'outfitter' | null): FeaturePersona {
  return orgType === 'outfitter' ? 'outfitter-owner' : 'club-manager'
}
